import { Controller } from "@hotwired/stimulus"
import { Vatio } from "@vatio-ai/sdk"
import { renderMarkdown } from "../lib/vatio_markdown"

// Chat with the tenant's Vatio agent, built on @vatio-ai/sdk (the SDK has no UI).
// Markup and copy come from shared/_vatio_chat; this wires it to the conversation.
//
// Two shapes: inline (/chat fills its container) and floating (launcher + panel
// on every consumer page, inside a data-turbo-permanent element). Floating keeps
// its conversation across Turbo visits: the chat object lives on the element and
// each connect re-attaches to it.
//
// Inline opens onto her past conversations (Vatio.conversations): one starts a
// new chat, a past one reopens it. A conversation only starts on her first
// message, so opening /chat does not make her a contact.
export default class extends Controller {
  static targets = [
    "launcher", "panel", "badge", "scroller", "messages", "empty", "typing", "input", "submit",
    "banner", "feedback", "feedbackQuestion", "feedbackChoices", "feedbackComment", "feedbackCommentInput", "name", "avatar", "userTemplate", "agentTemplate",
    "threads", "threadsLoading", "past", "threadList", "threadTemplate", "conversation"
  ]

  static values = {
    workspace: String,
    token: String,
    visitorToken: String,
    floating: Boolean,
    open: Boolean,
    connectingText: String,
    reconnectingText: String,
    sendErrorText: String,
    startErrorText: String,
    thanksText: String,
    untitledText: String,
    threadsErrorText: String
  }

  connect () {
    this.unsubscribers = []
    this.unread = 0
    this.generation = 0
    this.conversations = []
    this.fresh = false

    if (this.floatingValue) {
      this.onIdentify = () => this.identify()
      this.onKeydown = (event) => { if (event.key === "Escape" && this.openValue) this.close() }
      document.addEventListener("turbo:load", this.onIdentify)
      document.addEventListener("keydown", this.onKeydown)
      this.renderState()
      if (this.openValue) this.start()
    } else {
      this.loadBranding()
      this.browse({ initial: true })
    }
  }

  disconnect () {
    this.generation += 1
    this.unsubscribers.forEach((off) => off())
    this.unsubscribers = []
    this.chat = null

    if (this.floatingValue) {
      document.removeEventListener("turbo:load", this.onIdentify)
      document.removeEventListener("keydown", this.onKeydown)
    } else {
      this.element.vatioChat?.close()
      delete this.element.vatioChat
    }
  }

  // — Floating panel —

  toggle () {
    this.openValue ? this.close() : this.open()
  }

  open () {
    this.openValue = true
    this.unread = 0
    this.renderState()
    this.start().then(() => this.focusInput())
  }

  close () {
    this.openValue = false
    this.renderState()
    if (this.hasLauncherTarget) this.launcherTarget.focus()
  }

  renderState () {
    if (!this.hasPanelTarget) return

    this.panelTarget.hidden = !this.openValue
    if (this.hasLauncherTarget) {
      this.launcherTarget.setAttribute("aria-expanded", String(this.openValue))
      this.launcherTarget.classList.toggle("hidden", this.openValue && window.matchMedia("(max-width: 639px)").matches)
    }
    if (this.hasBadgeTarget) this.badgeTarget.hidden = this.unread === 0
  }

  // A new page can bring a different person (sign in / out): her conversation is
  // not this one's. The token itself changes on every page, so compare who it names.
  identify () {
    const token = document.getElementById("vatio-visitor")?.dataset.token || ""
    if (this.subjectOf(token) === this.subjectOf(this.visitorTokenValue)) return

    this.visitorTokenValue = token
    this.detach()
    if (this.openValue) this.start()
  }

  subjectOf (token) {
    try {
      const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")
      return JSON.parse(atob(payload)).sub || ""
    } catch {
      return ""
    }
  }

  // — Conversations list (inline) —

  // The list, newest first. On the first load with nothing to list she goes
  // straight to an empty conversation (or the one this tab still holds).
  async browse ({ initial = false } = {}) {
    this.showView("threads")
    const list = await this.loadThreads()
    if (!initial || !list || list.length > 0 || this.view !== "threads") return

    this.showView("conversation")
    if (Vatio.resumable({ workspace: this.workspaceValue, visitorToken: this.visitorTokenValue || null })) this.start()
  }

  // The conversations, or null when they would not load.
  async loadThreads () {
    if (!this.hasThreadListTarget) return null

    try {
      this.conversations = await Vatio.conversations({
        workspace: this.workspaceValue,
        token: this.tokenValue,
        visitorToken: this.visitorTokenValue || null
      })
    } catch (error) {
      // The list stays as it was; "Nueva conversación" still works.
      console.error("[vatio]", error)
      this.renderThreads()
      this.threadsLoadingTarget.textContent = this.threadsErrorTextValue
      this.threadsLoadingTarget.hidden = false
      return null
    }
    this.renderThreads()
    return this.conversations
  }

  renderThreads () {
    this.threadsLoadingTarget.hidden = true
    this.pastTarget.hidden = this.conversations.length === 0

    const rows = this.conversations.map((entry, index) => {
      const row = this.threadTemplateTarget.content.firstElementChild.cloneNode(true)
      row.dataset.index = index
      row.querySelector("[data-title]").textContent = entry.title || this.untitledTextValue
      row.querySelector("[data-when]").textContent = this.shortWhen(entry.updatedAt)
      row.querySelector("[data-preview]").textContent = entry.preview || ""
      if (this.chat && String(this.chat.chatId) === String(entry.chatId)) row.classList.add("bg-accent-softer")
      return row
    })
    this.threadListTarget.replaceChildren(...rows)
  }

  // "hace 3 h", "ayer", or a date once it is more than a week old.
  shortWhen (iso) {
    const at = Date.parse(iso || "")
    if (!at) return ""

    const seconds = Math.max(1, Math.round((Date.now() - at) / 1000))
    const units = [[60, "second", 1], [3600, "minute", 60], [86400, "hour", 3600], [604800, "day", 86400]]
    try {
      const unit = units.find(([limit]) => seconds < limit)
      if (unit) return new Intl.RelativeTimeFormat("es", { numeric: "auto", style: "short" }).format(-Math.floor(seconds / unit[2]), unit[1])
      return new Intl.DateTimeFormat("es", { day: "numeric", month: "short" }).format(new Date(at))
    } catch {
      return ""
    }
  }

  // An empty conversation; the chat itself starts with her first message.
  newConversation () {
    this.detach()
    this.fresh = true
    this.showView("conversation")
    this.focusInput()
  }

  openThread (event) {
    const entry = this.conversations[Number(event.currentTarget.dataset.index)]
    if (!entry) return

    this.showView("conversation")
    if (this.chat && String(this.chat.chatId) === String(entry.chatId)) return this.scrollToBottom()

    this.detach()
    this.start({ conversation: entry })
  }

  showView (view) {
    this.view = view
    if (this.hasThreadsTarget) this.threadsTarget.hidden = view !== "threads"
    if (this.hasConversationTarget) this.conversationTarget.hidden = view !== "conversation"
    this.showBanner(null)
    if (view === "conversation") this.scrollToBottom()
  }

  // — Conversation —

  start (options = {}) {
    if (this.starting) return this.starting

    const starting = this.connectChat(options).finally(() => {
      if (this.starting === starting) this.starting = null
    })
    this.starting = starting
    return starting
  }

  // Lets go of the conversation on screen (another person, another thread).
  // A connect still in flight sees the generation change and drops its chat.
  detach () {
    this.generation += 1
    this.unsubscribers.forEach((off) => off())
    this.unsubscribers = []
    this.element.vatioChat?.close()
    delete this.element.vatioChat
    this.chat = null
    this.starting = null
    this.fresh = false
    this.resetMessages()
  }

  async connectChat ({ conversation = null } = {}) {
    if (this.chat) return

    const generation = this.generation
    const stale = () => generation !== this.generation
    this.showBanner(this.connectingTextValue)
    try {
      const chat = this.element.vatioChat || await Vatio.chat({
        workspace: this.workspaceValue,
        token: this.tokenValue,
        visitorToken: this.visitorTokenValue || null,
        fresh: this.fresh,
        conversation
      })
      if (stale()) return chat.close()

      this.element.vatioChat = chat
      this.chat = chat
      this.fresh = false

      this.unsubscribers.push(
        chat.on("message", (message) => this.receive(message)),
        chat.on("update", (update) => this.update(update)),
        chat.on("typing", (typing) => this.showTyping(typing)),
        chat.on("feedback", (feedback) => this.showFeedback(feedback)),
        chat.on("status", (status) => {
          this.showBanner(status === "reconnecting" || status === "polling" ? this.reconnectingTextValue : null)
        })
      )

      this.resetMessages()
      const history = await chat.history({ limit: 50 })
      if (stale()) return
      history.forEach((message) => this.render(message))
      this.showBanner(null)
      // The SDK fetches a pending feedback moment when the subscription confirms,
      // before we listen: ask for it.
      const feedback = await chat.feedback().catch(() => null)
      if (stale()) return
      this.showFeedback(feedback)
      if (this.floatingValue) this.loadBranding()
      this.scrollToBottom()
    } catch (error) {
      if (stale()) return
      console.error("[vatio]", error)
      this.showBanner(this.startErrorTextValue, true)
    }
  }

  async loadBranding () {
    try {
      const config = await Vatio.config({ workspace: this.workspaceValue, token: this.tokenValue })
      const name = config.name || config.agent_name
      const avatar = config.avatar_url || config.avatar
      if (name && this.hasNameTarget) this.nameTargets.forEach((el) => { el.textContent = name })
      if (avatar && this.hasAvatarTarget) {
        this.avatarTargets.forEach((el) => { el.src = avatar; el.hidden = false })
      }
    } catch {
      // The header keeps its default title.
    }
  }

  async submit (event) {
    event.preventDefault()
    const text = this.inputTarget.value.trim()
    if (!text) return

    this.inputTarget.value = ""
    this.resize()
    this.updateSubmit()
    this.hideFeedback()

    try {
      await this.start()
      if (!this.chat) throw new Error("chat not started")

      const result = await this.chat.send(text)
      this.render(result.message || { id: result.user_message_id, role: "user", content: text })
    } catch (error) {
      console.error("[vatio]", error)
      this.inputTarget.value = text
      this.resize()
      this.updateSubmit()
      this.showBanner(this.sendErrorTextValue, true)
    }
  }

  receive (message) {
    this.render(message)
    if (message.role !== "user" && this.floatingValue && !this.openValue) {
      this.unread += 1
      this.renderState()
    }
  }

  update ({ id, content }) {
    const bubble = this.messagesTarget.querySelector(`[data-message-id="${id}"] [data-body]`)
    if (bubble && content) bubble.innerHTML = renderMarkdown(content)
  }

  // — Rendering —

  render (message) {
    const id = Number(message.id)
    const attachments = message.attachments || []
    if (!message.content && attachments.length === 0) return
    if (id && this.messagesTarget.querySelector(`[data-message-id="${id}"]`)) return

    const template = message.role === "user" ? this.userTemplateTarget : this.agentTemplateTarget
    const row = template.content.firstElementChild.cloneNode(true)
    row.dataset.messageId = id || ""

    const body = row.querySelector("[data-body]")
    body.innerHTML = message.content ? renderMarkdown(message.content) : ""
    attachments.forEach((attachment) => body.append(this.attachmentNode(attachment)))

    const next = id && [...this.messagesTarget.children].find((child) => Number(child.dataset.messageId) > id)
    this.messagesTarget.insertBefore(row, next || null)

    if (this.hasEmptyTarget) this.emptyTarget.hidden = true
    this.scrollToBottom()
  }

  attachmentNode ({ kind, url, filename }) {
    if (!url) return document.createTextNode(filename || "")

    if (kind === "image") {
      const img = document.createElement("img")
      img.src = url
      img.alt = filename || ""
      img.loading = "lazy"
      img.className = "mt-2 max-h-60 rounded-lg"
      return img
    }

    const anchor = document.createElement("a")
    anchor.href = url
    anchor.target = "_blank"
    anchor.rel = "noopener noreferrer"
    anchor.textContent = filename || url
    anchor.className = "mt-2 block underline underline-offset-2 break-all"
    return anchor
  }

  resetMessages () {
    this.messagesTarget.replaceChildren()
    if (this.hasEmptyTarget) this.emptyTarget.hidden = false
    this.showTyping(false)
    this.hideFeedback()
  }

  showTyping (typing) {
    this.typingTarget.hidden = !typing
    if (typing) this.scrollToBottom()
  }

  showBanner (text, isError = false) {
    if (!this.hasBannerTarget) return

    this.bannerTarget.hidden = !text
    this.bannerTarget.textContent = text || ""
    this.bannerTarget.classList.toggle("text-danger", isError)
    this.bannerTarget.classList.toggle("text-ink-muted", !isError)
  }

  scrollToBottom () {
    requestAnimationFrame(() => {
      this.scrollerTarget.scrollTop = this.scrollerTarget.scrollHeight
    })
  }

  focusInput () {
    if (this.hasInputTarget) this.inputTarget.focus({ preventScroll: true })
  }

  // — Composer —

  keydown (event) {
    if (event.key !== "Enter" || event.shiftKey || event.isComposing) return

    event.preventDefault()
    this.inputTarget.form.requestSubmit()
  }

  input () {
    this.resize()
    this.updateSubmit()
  }

  resize () {
    const field = this.inputTarget
    field.style.height = "auto"
    field.style.height = `${Math.min(field.scrollHeight, 128)}px`
  }

  updateSubmit () {
    this.submitTarget.disabled = this.inputTarget.value.trim() === ""
  }

  // — Feedback (the agent asks how the conversation went) —

  showFeedback (feedback) {
    if (!this.hasFeedbackTarget) return
    if (!feedback || feedback.rating || feedback.dismissed) return this.hideFeedback()

    if (feedback.question) this.feedbackQuestionTarget.textContent = feedback.question
    this.feedbackChoicesTarget.hidden = false
    this.feedbackCommentTarget.hidden = true
    this.feedbackCommentInputTarget.value = ""
    this.feedbackTarget.hidden = false
    this.scrollToBottom()
  }

  hideFeedback () {
    if (this.hasFeedbackTarget) this.feedbackTarget.hidden = true
  }

  // "good" is sent at once; "neutral" and "bad" first ask what went wrong (optional).
  rate (event) {
    const rating = event.currentTarget.dataset.rating
    if (rating === "good") return this.sendRating(rating)

    this.pendingRating = rating
    this.feedbackChoicesTarget.hidden = true
    this.feedbackCommentTarget.hidden = false
    this.feedbackCommentInputTarget.focus({ preventScroll: true })
    this.scrollToBottom()
  }

  sendComment (event) {
    event.preventDefault()
    this.sendRating(this.pendingRating, this.feedbackCommentInputTarget.value.trim())
  }

  async sendRating (rating, comment = "") {
    this.hideFeedback()
    try {
      await this.chat?.rate(rating, comment ? { comment } : {})
      this.showBanner(this.thanksTextValue)
      setTimeout(() => this.showBanner(null), 3000)
    } catch (error) {
      // "feedback_unavailable": the moment passed while she was answering.
      if (error?.code !== "feedback_unavailable") console.error("[vatio]", error)
    }
  }

  async dismissFeedback () {
    this.hideFeedback()
    try {
      await this.chat?.dismissFeedback()
    } catch (error) {
      console.error("[vatio]", error)
    }
  }
}
